import { Button, CloseIcon, MenuIcon } from "@eworkit/iris";
import { isFunction } from "lodash-es";
import React, { FC, useCallback, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useFeatureFlags } from "@/components/FeatureFlags";
import { FullScreenDropdown, MenuItem, MobileNestedMenu } from "@/components/Navigation";
import { UserContext } from "@/components/User";
import { isProduction } from "@/core/environments";
import { FEATURE_FLAG } from "@/data/feature-flags";
import { Flex, Icon } from "@/eworkitComponents";
import { EworkPlus } from "@/views/components/Layout/components/Navigation/EworkPlus";
import { INavigationProps } from "@/views/components/Layout/components/Navigation/Navigation";
import { ChatRouteNavItem } from "@/views/components/Layout/components/Topbar/ChatRouteNavItem";
import { RouteNavItem } from "@/views/components/Layout/components/Topbar/components/RouteNavItem";
import { TasksNavItem } from "@/views/components/Layout/components/Topbar/components/TasksNavItem";
import { NotificationsRouteNavItem } from "@/views/components/Layout/components/Topbar/NotificationsRouteNavItem";
import { router } from "@/views/router";

import { HelpCenter } from "./HelpCenter";
import { TimeReports } from "./TimeReport";
import { useContextDropdown } from "./useContextDropdown";

interface INavigationMobileProps extends INavigationProps {}

export const NavigationMobile: FC<INavigationMobileProps> = () => {
  const User = useContext(UserContext);
  const isManager = User.hasCompanyManagerPrivileges();
  const profiles = useContextDropdown().connectedProfiles;

  const { isFeatureEnabled } = useFeatureFlags();

  const { t } = useTranslation("session");

  const baseClass = "navigation";

  const dashboardRoute = router.get("dashboard");
  const jobRequestsRoute = router.get("job-requests");
  const jobApplicationsRoute = router.get("job-applications");
  const assignmentsRoute = router.get("assignments");
  const myTeamRoute = router.get("companies.team");
  const companySettingsRoute = router.get("company.settings");
  const tasksRoute = router.get("tasks");
  const timeReportsRoute = router.get("time-reports");
  const contractsRoute = router.get("contracts");

  const allNotificationCount = profiles.reduce(
    (acc, curr) => (curr.notDisplayedNotificationCount !== null ? acc + curr.notDisplayedNotificationCount : 0),
    0,
  );
  const allTaskCount = profiles.reduce(
    (acc, curr) => (curr.notDisplayedTaskCount !== null ? curr.notDisplayedTaskCount + acc : 0),
    0,
  );

  const hasUnreadNotificationOrTasks = profiles && (allNotificationCount > 0 || allTaskCount > 0);

  const fld: any = useRef(null);
  const handleClose = useCallback(() => {
    const closeButton: any = document.querySelector(`[aria-controls="${fld?.current?.id}"]`);
    if (closeButton && isFunction(closeButton.click)) {
      closeButton.click();
    }
  }, []);

  const handleJobsClick = useCallback(() => {
    handleClose();
  }, [handleClose]);

  const DropdownTarget = ({ onClick, ...restDropdownTargetProps }: any) => {
    const onClickHandler = useCallback(e => {
      const contextDropdownOpen = document.getElementById("context-dropdown-close");
      if (contextDropdownOpen) {
        contextDropdownOpen.click();
      }
      onClick(e);
    }, []);
    return <Button {...restDropdownTargetProps} onClick={onClickHandler} />;
  };

  return (
    <Flex padding={[false, false, false, "sm"]}>
      <FullScreenDropdown
        ref={fld}
        as={DropdownTarget}
        size="xxl"
        hideOnClickOutside={true}
        openDisclosureProps={{ iconLeft: <CloseIcon id="navigation-close-menu-icon" />, variant: "ghost" }}
        closedDisclosureProps={{
          iconLeft: hasUnreadNotificationOrTasks ? (
            <div style={{ position: "relative" }}>
              <div style={{ position: "absolute", right: 0, bottom: 12 }}>
                <Icon icon="circle" color="link" size="xxs" />
              </div>
              <MenuIcon id="navigation-menu-icon" />
            </div>
          ) : (
            <div>
              <MenuIcon id="navigation-menu-icon" />
            </div>
          ),
          variant: "ghost",
        }}>
        <Flex direction="column" background="secondary">
          <Flex style={{ minHeight: 370 }} direction="column" padding={["md", "md", "xs", "md"]}>
            <Flex direction="row" style={{ marginLeft: "-16px" }} innerPadding="lg">
              {User.hasRouteAccess(tasksRoute) && <TasksNavItem onClick={handleClose} hasActiveState={false} />}
              <NotificationsRouteNavItem onClick={handleClose} />
              {isFeatureEnabled(FEATURE_FLAG.MESSAGE_CENTER) &&
                (!User.contextCompany || User.hasCompanyManagerPrivileges()) && (
                  <ChatRouteNavItem onClick={handleClose} />
                )}
            </Flex>

            <MenuItem
              as={RouteNavItem}
              path={dashboardRoute.path}
              exact={false}
              variant="mobile"
              text={t("COMPONENTS.TOPBAR.BUTTON_DASHBOARD")}
              testName={dashboardRoute.testName}
              onClick={handleClose}
            />

            <MenuItem
              as={RouteNavItem}
              path={jobRequestsRoute.path}
              exact={false}
              variant="mobile"
              text={t("COMPONENTS.TOPBAR.BUTTON_JOB_REQUESTS")}
              onClick={handleJobsClick}
            />

            <MenuItem
              as={MobileNestedMenu}
              size="md"
              key="view"
              buttonContent={t("COMPONENTS.TOPBAR.BUTTON_VIEW")}
              className={`${baseClass}--mobile-nested`}>
              <Flex
                style={{ minHeight: 250 }}
                direction="column"
                padding={["sm", false, false, false]}
                innerPadding={"sm"}>
                {User.hasRouteAccess(assignmentsRoute) && (
                  <RouteNavItem
                    path={assignmentsRoute.path}
                    exact={false}
                    variant="nested-mobile"
                    icon="type_assignment"
                    text={t("COMPONENTS.TOPBAR.BUTTON_ASSIGNMENTS")}
                    onClick={handleClose}
                  />
                )}
                <RouteNavItem
                  path={jobApplicationsRoute.path}
                  exact={false}
                  variant="nested-mobile"
                  icon="type_application"
                  text={t("COMPONENTS.TOPBAR.BUTTON_APPLICATIONS")}
                  onClick={handleClose}
                />
                {!isProduction && User.contextCompany && (
                  <RouteNavItem
                    path={timeReportsRoute.path}
                    exact={false}
                    variant="nested-mobile"
                    icon="type_time_report"
                    text={t("COMPONENTS.TOPBAR.BUTTON_TIME_REPORTS")}
                    onClick={handleClose}
                  />
                )}
                {User.hasRouteAccess(contractsRoute) && (
                  <RouteNavItem
                    path={contractsRoute.path}
                    exact={false}
                    variant="nested-mobile"
                    icon="type_application"
                    text={t("COMPONENTS.TOPBAR.BUTTON_CONTRACTS")}
                    onClick={handleClose}
                  />
                )}
              </Flex>
            </MenuItem>

            {isManager && (
              <MenuItem
                as={MobileNestedMenu}
                size="md"
                key="company"
                buttonContent={t("COMPONENTS.TOPBAR.BUTTON_MY_COMPANY")}
                className={`${baseClass}--mobile-nested`}>
                <Flex
                  style={{ minHeight: 170 }}
                  direction="column"
                  padding={["sm", false, false, false]}
                  innerPadding={"sm"}>
                  <RouteNavItem
                    path={myTeamRoute.path}
                    exact={false}
                    variant="nested-mobile"
                    icon="users"
                    text={t("COMPONENTS.TOPBAR.BUTTON_MY_TEAM")}
                    testName={myTeamRoute.testName}
                    onClick={handleClose}
                  />
                  <RouteNavItem
                    path={companySettingsRoute.path}
                    exact={false}
                    variant="nested-mobile"
                    icon="settings"
                    text={t("COMPONENTS.TOPBAR.BUTTON_SETTINGS")}
                    onClick={handleClose}
                  />
                </Flex>
              </MenuItem>
            )}

            <EworkPlus variant="mobile" />
            <TimeReports variant="mobile" />
            <HelpCenter variant="mobile" />
          </Flex>
        </Flex>
      </FullScreenDropdown>
    </Flex>
  );
};
